import * as React from "react";
import { Button } from "./Button";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Optional slot rendered between description and footer. Receives focus trap participation automatically. */
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: "primary" | "destructive";
  /** When true, the Confirm button is disabled (in addition to internal pending). */
  confirmDisabled?: boolean;
  /** May return a Promise — internal pending state spans until it resolves/rejects.
   *  Modal does NOT auto-close on success; consumer calls onOpenChange(false). */
  onConfirm: () => void | Promise<void>;
  /** External pending signal. ORed with internal pending. */
  loading?: boolean;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "primary",
  confirmDisabled = false,
  onConfirm,
  loading = false,
}: ConfirmDialogProps) {
  const [internalPending, setInternalPending] = React.useState(false);
  const pending = internalPending || loading;
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const previousFocusRef = React.useRef<HTMLElement | null>(null);
  const mountedRef = React.useRef(true);
  React.useEffect(() => () => { mountedRef.current = false; }, []);
  const titleId = React.useId();
  const descriptionId = React.useId();

  React.useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const node = dialogRef.current;
    if (node) {
      const focusable = node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const first = focusable[0];
      first?.focus();
    }

    return () => {
      previousFocusRef.current?.focus?.();
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (pending) return;
        e.preventDefault();
        onOpenChange(false);
        return;
      }
      if (e.key === "Tab") {
        const node = dialogRef.current;
        if (!node) return;
        const focusable = Array.from(
          node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => !el.hasAttribute("disabled"));
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !node.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !node.contains(active)) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, pending, onOpenChange]);

  if (!open) return null;

  const handleConfirm = async () => {
    if (pending || confirmDisabled) return;
    setInternalPending(true);
    try {
      await Promise.resolve(onConfirm());
    } catch {
      // Keep modal open on error — consumer decides what to do.
    } finally {
      if (mountedRef.current) setInternalPending(false);
    }
  };

  const handleBackdropClick = () => {
    if (pending) return;
    onOpenChange(false);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/40"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={handleBackdropClick}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
          className="w-full max-w-md rounded-lg border border-border bg-card shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4">
            <h2
              id={titleId}
              className="text-base font-semibold text-card-foreground"
            >
              {title}
            </h2>
            {description && (
              <p
                id={descriptionId}
                className="mt-2 text-sm leading-relaxed text-muted-foreground"
              >
                {description}
              </p>
            )}
            {body && (
              <div className="mt-3">{body}</div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/50 px-6 py-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {cancelLabel}
            </Button>
            <Button
              variant={confirmVariant}
              size="sm"
              onClick={handleConfirm}
              loading={pending}
              disabled={confirmDisabled}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
